import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Chip, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import RetryNotice from './RetryNotice';
import { departmentLabel } from './departments';
import WarningLoading from './WarningLoading';
import apiRequest from '../../api/request';
import FlagSummary from './FlagSummary';
import WarningMetricTable from './WarningMetricTable';
import useConfigAccess from './useConfigAccess';
import { LEAD_CATEGORY_ORDER } from './leadCategories';
import useLeadDrilldown from './useLeadDrilldown';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

const StatusCell = ({ isWarning }) =>
  isWarning ? (
    <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />
  ) : (
    <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />
  );

// LTA results for the selected month - the leads assigned to a person in
// the period, checked against admissions by period end (see
// lta_dashboard.controller.js). Same threshold resolution and sort as
// LCR/ACR.
const LtaDashboard = () => {
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const drill = useLeadDrilldown('lta', month); // a click on a lead count opens the leads behind it
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalEvaluated: 0, warningCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `warning-dashboard/lta-dashboard?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this check a server error
        // rendered as an empty page (or zeros) instead of a message
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load LTA results');
          return;
        }
        setRows(res?.data?.rows || []);
        setSummary({
          totalEvaluated: res?.data?.totalEvaluated || 0,
          warningCount: res?.data?.warningCount || 0,
        });
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load LTA results');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  const dangerScore = (row) => (row.threshold === null ? -Infinity : row.threshold - row.value);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => dangerScore(b) - dangerScore(a)),
    [rows]
  );

  const columns = [
    { field: 'status', headerName: 'Status', align: 'center', render: (row) => <StatusCell isWarning={row.isWarning} />, csvValue: (row) => (row.isWarning ? 'Warning' : 'OK') },
    {
      field: 'entityName',
      headerName: 'Name',
      render: (row) => (
        <Typography
          variant="body2"
          fontStyle={row.isActive === false ? 'italic' : 'normal'}
          color={row.isActive === false || row.isWarning ? 'error.main' : 'text.primary'}
        >
          {row.entityName}
          {row.isActive === false && ' (Inactive)'}
        </Typography>
      ),
    },
    { field: 'departmentId', headerName: 'Department', render: (row) => departmentName(row.departmentId), csvValue: (row) => departmentName(row.departmentId) },
    {
      field: 'leadCategory',
      headerName: 'Lead Category',
      render: (row) => (row.leadCategory === 'Uncategorized' ? <Chip size="small" variant="outlined" label="Uncategorized" /> : row.leadCategory),
    },
    { field: 'leadsCount', headerName: 'Leads Assigned', align: 'right', render: drill.cell('assigned', 'leadsCount') },
    { field: 'admissionsCount', headerName: 'Admissions', align: 'right', render: drill.cell('admitted', 'admissionsCount') },
    {
      field: 'value',
      headerName: 'LTA',
      align: 'right',
      render: (row) => (
        <Typography variant="body2" fontWeight={row.isWarning ? 700 : 400} color={row.isWarning ? 'error.main' : 'text.primary'}>
          {row.value}%
        </Typography>
      ),
      csvValue: (row) => `${row.value}%`,
    },
    {
      field: 'threshold',
      headerName: 'Threshold',
      align: 'right',
      // "dept default" = the category has no threshold of its own, so the department's Default is what applied
      render: (row) =>
        row.threshold === null ? (
          <Typography variant="body2" color="text.secondary">{row.thresholdOff ? 'Switched off' : 'Not configured'}</Typography>
        ) : (
          <span>
            {`< ${row.threshold}%`}
            {row.thresholdSource === 'default' && <Typography component="span" variant="caption" color="text.secondary"> · dept default</Typography>}
          </span>
        ),
      csvValue: (row) => (row.threshold === null ? (row.thresholdOff ? 'Switched off' : 'Not configured') : `< ${row.threshold}%${row.thresholdSource === 'default' ? ' (dept default)' : ''}`),
    },
  ];

  const { canConfigure } = useConfigAccess();

  return (
    <WarningPage
      title="LTA - Lead to Admission Rate"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">
            Back to Warning Dashboard
          </Button>
          {canConfigure('lta-config') && (
            <Button component={Link} to={`/dashboard/warning-dashboard/lta-config?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
              Configure Thresholds
            </Button>
          )}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && (
        <FlagSummary warningCount={summary.warningCount} totalEvaluated={summary.totalEvaluated} />
      )}

      {!loading && !error && (
        <WarningMetricTable
          rows={sortedRows}
          columns={columns}
          departmentName={departmentName}
          emptyMessage="No leads assigned this period."
          exportFileName="LTA"
          rowKey={(row) => `${row.entityId}-${row.leadCategory}`}
          categoryField="leadCategory"
          categoryOrder={LEAD_CATEGORY_ORDER}
        />
      )}
      {drill.dialog}
    </WarningPage>
  );
};

export default LtaDashboard;
